/**
 * Video Lightbox Component JavaScript
 * video-lightbox.js
 * Handles featured video playback in fullscreen lightbox
 */

(function (global) {
  "use strict";

  /**
   * Video Lightbox Class
   */
  function VideoLightbox(options) {
    this.options = Object.assign(
      {
        triggerSelector: ".video-play-btn, .video-preview",
        lightboxClass: "video-lightbox",
        activeClass: "is-active",
        loadingClass: "is-loading",
        autoplay: true,
        controls: true,
        onOpen: null,
        onClose: null,
        onError: null,
      },
      options || {}
    );

    this.lightbox = null;
    this.player = null;
    this.isOpen = false;
    this.init();
  }

  VideoLightbox.prototype.init = function () {
    this.bindTriggers();
    this.setupKeyboardControls();
  };

  /**
   * Bind click events to trigger elements
   */
  VideoLightbox.prototype.bindTriggers = function () {
    const self = this;

    document.addEventListener("click", function (e) {
      // Check if clicked element or parent matches trigger selector
      const trigger = e.target.closest(self.options.triggerSelector);
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();

        // Extract video data
        const videoData = {
          provider: trigger.dataset.videoProvider || "html5",
          url: trigger.dataset.videoUrl,
          title: trigger.dataset.videoTitle || "",
          description: trigger.dataset.videoDescription || "",
          runtime: trigger.dataset.videoRuntime || "",
        };

        if (videoData.url) {
          self.open(videoData);
        }
      }
    });
  };

  /**
   * Open lightbox with video
   */
  VideoLightbox.prototype.open = function (videoData) {
    if (this.isOpen) return;

    this.isOpen = true;
    this.createLightbox(videoData);
    document.body.appendChild(this.lightbox);

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Trigger open callback
    if (this.options.onOpen) {
      this.options.onOpen(videoData);
    }

    // Add active class after render
    requestAnimationFrame(() => {
      this.lightbox.classList.add(this.options.activeClass);
    });

    // Focus management
    this.trapFocus();
  };

  /**
   * Create lightbox HTML
   */
  VideoLightbox.prototype.createLightbox = function (videoData) {
    const lightbox = document.createElement("div");
    lightbox.className = this.options.lightboxClass;
    lightbox.setAttribute("role", "dialog");
    lightbox.setAttribute("aria-modal", "true");
    lightbox.setAttribute("aria-label", videoData.title || "Video player");

    // Create HTML structure
    lightbox.innerHTML = `
      <div class="${this.options.lightboxClass}__overlay"></div>
      <div class="${this.options.lightboxClass}__content">
        <button class="${this.options.lightboxClass}__close"
                aria-label="Close video"
                title="Close (Esc)">
        </button>
        <div class="${this.options.lightboxClass}__player ${
      this.options.loadingClass
    }">
          ${this.createPlayerHTML(videoData)}
        </div>
        ${
          videoData.title || videoData.description
            ? `
          <div class="${this.options.lightboxClass}__info">
            ${
              videoData.title
                ? `<div class="${this.options.lightboxClass}__info-title">${videoData.title}</div>`
                : ""
            }
            ${
              videoData.description
                ? `<div class="${this.options.lightboxClass}__info-description">${videoData.description}</div>`
                : ""
            }
          </div>
        `
            : ""
        }
      </div>
    `;

    this.lightbox = lightbox;
    this.bindLightboxEvents();
  };

  /**
   * Create player HTML based on provider
   */
  VideoLightbox.prototype.createPlayerHTML = function (videoData) {
    const { provider, url, title } = videoData;

    switch (provider) {
      case "youtube":
        const youtubeId = this.extractYouTubeId(url);
        if (youtubeId) {
          return `<iframe
            src="https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&fs=1"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowfullscreen
            title="${title || "YouTube video"}"></iframe>`;
        }
        break;

      case "vimeo":
        const vimeoId = this.extractVimeoId(url);
        if (vimeoId) {
          return `<iframe
            src="https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
            title="${title || "Vimeo video"}"></iframe>`;
        }
        break;

      case "html5":
      default:
        // Support multiple video formats
        const videoExtension = this.getVideoExtension(url);
        const mimeType = this.getMimeType(videoExtension);

        return `<video
          controls
          ${this.options.autoplay ? "autoplay" : ""}
          controlsList="nodownload"
          title="${title || "Video"}">
          <source src="${url}" type="${mimeType}">
          <p>Your browser doesn't support HTML5 video.</p>
        </video>`;
    }

    // Error state
    return this.createErrorHTML("Unable to load video");
  };

  /**
   * Create error HTML
   */
  VideoLightbox.prototype.createErrorHTML = function (message) {
    return `
      <div class="${this.options.lightboxClass}__error">
        <div class="${this.options.lightboxClass}__error-icon">
          <svg viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
        </div>
        <div class="${this.options.lightboxClass}__error-message">${message}</div>
        <div class="${this.options.lightboxClass}__error-details">Please try again or contact support if the problem persists.</div>
      </div>
    `;
  };

  /**
   * Bind lightbox events
   */
  VideoLightbox.prototype.bindLightboxEvents = function () {
    const self = this;

    // Close button
    const closeBtn = this.lightbox.querySelector(
      `.${this.options.lightboxClass}__close`
    );
    if (closeBtn) {
      closeBtn.addEventListener("click", function () {
        self.close();
      });
    }

    // Overlay click
    const overlay = this.lightbox.querySelector(
      `.${this.options.lightboxClass}__overlay`
    );
    if (overlay) {
      overlay.addEventListener("click", function () {
        self.close();
      });
    }

    // Remove loading class when content loads
    const player = this.lightbox.querySelector(
      `.${this.options.lightboxClass}__player`
    );
    const video = player.querySelector("video");
    const iframe = player.querySelector("iframe");

    if (video) {
      video.addEventListener("loadeddata", function () {
        player.classList.remove(self.options.loadingClass);
      });

      video.addEventListener("error", function (e) {
        self.handleError(e);
      });
    } else if (iframe) {
      iframe.addEventListener("load", function () {
        player.classList.remove(self.options.loadingClass);
      });
    }
  };

  /**
   * Close lightbox
   */
  VideoLightbox.prototype.close = function () {
    if (!this.isOpen) return;

    const self = this;

    // Remove active class for animation
    this.lightbox.classList.remove(this.options.activeClass);

    // Restore body scroll
    document.body.style.overflow = "";

    // Remove after animation
    setTimeout(function () {
      if (self.lightbox && self.lightbox.parentNode) {
        self.lightbox.parentNode.removeChild(self.lightbox);
      }
      self.lightbox = null;
      self.isOpen = false;
    }, 300);

    // Trigger close callback
    if (this.options.onClose) {
      this.options.onClose();
    }
  };

  /**
   * Handle errors
   */
  VideoLightbox.prototype.handleError = function (error) {
    const player = this.lightbox.querySelector(
      `.${this.options.lightboxClass}__player`
    );
    if (player) {
      player.classList.remove(this.options.loadingClass);
      player.innerHTML = this.createErrorHTML("Failed to load video");
    }

    if (this.options.onError) {
      this.options.onError(error);
    }
  };

  /**
   * Setup keyboard controls
   */
  VideoLightbox.prototype.setupKeyboardControls = function () {
    const self = this;

    document.addEventListener("keydown", function (e) {
      if (self.isOpen) {
        // ESC to close
        if (e.key === "Escape") {
          e.preventDefault();
          self.close();
        }
        // Tab trap
        else if (e.key === "Tab") {
          self.handleTabKey(e);
        }
      }
    });
  };

  /**
   * Trap focus within lightbox
   */
  VideoLightbox.prototype.trapFocus = function () {
    const closeBtn = this.lightbox.querySelector(
      `.${this.options.lightboxClass}__close`
    );
    if (closeBtn) {
      closeBtn.focus();
    }
  };

  /**
   * Handle tab key for focus trap
   */
  VideoLightbox.prototype.handleTabKey = function (e) {
    const focusableElements = this.lightbox.querySelectorAll(
      'button, [href], input, select, textarea, video, iframe, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  };

  /**
   * Extract YouTube video ID
   */
  VideoLightbox.prototype.extractYouTubeId = function (url) {
    const regex =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regex);
    return match && match[7].length === 11 ? match[7] : null;
  };

  /**
   * Extract Vimeo video ID
   */
  VideoLightbox.prototype.extractVimeoId = function (url) {
    const regex =
      /(?:vimeo)\.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const match = url.match(regex);
    return match ? match[3] : null;
  };

  /**
   * Get video file extension
   */
  VideoLightbox.prototype.getVideoExtension = function (url) {
    const match = url.match(/\.([^.]+)$/);
    return match ? match[1].toLowerCase() : "mp4";
  };

  /**
   * Get MIME type for video
   */
  VideoLightbox.prototype.getMimeType = function (extension) {
    const types = {
      mp4: "video/mp4",
      webm: "video/webm",
      ogg: "video/ogg",
      ogv: "video/ogg",
      mov: "video/quicktime",
      avi: "video/x-msvideo",
      mkv: "video/x-matroska",
    };
    return types[extension] || "video/mp4";
  };

  /**
   * Static initialization method
   */
  VideoLightbox.init = function (options) {
    return new VideoLightbox(options);
  };

  // Export to global
  global.VideoLightbox = VideoLightbox;

  // Auto-initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      global.videoLightbox = new VideoLightbox();
    });
  } else {
    global.videoLightbox = new VideoLightbox();
  }

  // jQuery compatibility
  if (typeof jQuery !== "undefined") {
    jQuery.fn.videoLightbox = function (options) {
      return this.each(function () {
        const $el = jQuery(this);
        if (!$el.data("videoLightbox")) {
          $el.data(
            "videoLightbox",
            new VideoLightbox(
              jQuery.extend({}, options, { triggerSelector: this })
            )
          );
        }
      });
    };
  }
})(typeof window !== "undefined" ? window : this);
